import { Check } from "lucide-react";
import { usd, planBasis, PLAN_KEYS } from "@/lib/pricing";

export function IntervalToggle({ iv, setIv, savings, dark }) {
  const b = (v, label, id) => (
    <button type="button" role="radio" aria-checked={iv === v} onClick={() => setIv(v)} data-testid={id}
      className={`h-10 px-5 rounded-full text-sm font-medium transition-colors ${iv === v ? (dark ? "bg-white text-stone-900" : "bg-stone-900 text-white") : ""}`}>{label}</button>
  );
  return (
    <div role="radiogroup" aria-label="Billing interval" className={`inline-flex rounded-full border p-1 ${dark ? "border-white/20" : "border-[#E6E1D7] bg-white"}`} data-testid="interval-toggle">
      {b("month", "Monthly", "interval-month")}{b("year", `Annual · save ${savings}%`, "interval-year")}
    </div>
  );
}

export function PlanCard({ p, iv, cta, onChoose, current, disabled, testid }) {
  const price = iv === "year" ? p.year_cents : p.month_cents;
  const pop = p.popular;
  return (
    <div data-testid={testid || `plan-card-${p.key}`} className={`min-w-0 rounded-2xl p-5 sm:p-7 flex flex-col transition-transform duration-300 hover:-translate-y-1 ${pop ? "bg-[#1C1917] text-stone-300 md:-translate-y-3 md:hover:-translate-y-4 shadow-xl shadow-stone-900/20" : "bg-white border border-[#E6E1D7]"}`}>
      <div className="flex items-center justify-between gap-2">
        <div className={`font-display text-2xl ${pop ? "text-white" : ""}`}>{p.name}</div>
        {pop && <span className={`text-[11px] uppercase tracking-wider rounded-full px-3 py-1 ${pop ? "bg-[#9E2A2B] text-white" : ""}`} data-testid="recommended-badge">Recommended for full-service restaurants</span>}
        {current && <span className="text-[11px] uppercase tracking-wider bg-emerald-600 text-white rounded-full px-3 py-1">Current</span>}
      </div>
      <p className={`text-sm mt-2 ${pop ? "text-stone-400" : "text-stone-500"}`}>{p.best_for}</p>
      <div className="mt-6"><span className={`text-5xl font-semibold num ${pop ? "text-white" : ""}`} data-testid={`plan-price-${p.key}`}>{usd(price)}</span></div>
      <div className="text-xs mt-1 opacity-80">{planBasis(p, iv)}</div>
      {iv === "year" && <div className="text-xs mt-1 text-[#c9826c]">≈ {usd(p.year_per_month_cents)}/mo equivalent</div>}
      <p className={`mt-5 text-sm font-medium ${pop ? "text-white" : "text-stone-900"}`}>{p.tagline}</p>
      <ul className="mt-5 space-y-2.5 text-sm flex-1">{(p.highlights || []).map((i) => <li key={i} className="flex gap-2"><Check className="h-4 w-4 mt-0.5 shrink-0 text-[#c9826c]" />{i}</li>)}</ul>
      <div className="text-xs mt-5 opacity-70">{p.support}</div>
      {cta && <button onClick={() => onChoose(p.key)} disabled={disabled} data-testid={`plan-cta-${p.key}`}
        className={`mt-6 h-12 rounded-full font-semibold transition-colors disabled:opacity-50 ${pop ? "bg-white text-stone-900 hover:bg-stone-100" : "bg-stone-900 text-white hover:bg-stone-800"}`}>{cta(p)}</button>}
    </div>
  );
}

export function PlanGrid({ pricing, iv, cta, onChoose, currentKey, disabledKey }) {
  return (
    <div className="grid md:grid-cols-3 gap-6 items-start">
      {PLAN_KEYS.map((k) => { const p = pricing.plans.find((x) => x.key === k); return p && (
        <PlanCard key={k} p={p} iv={iv} cta={cta} onChoose={onChoose} current={currentKey === k} disabled={disabledKey?.(k)} />); })}
    </div>
  );
}

export function FoundingBanner({ pricing, iv, onChoose, cta = "Claim founding pricing" }) {
  const f = pricing.founding;
  const p = pricing.plans.find((x) => x.key === "founding");
  if (!f.open || !p) return null;
  return (
    <div className="rounded-2xl border-2 border-[#9E2A2B] bg-[#FBF1EC] p-6 md:p-8 flex flex-col md:flex-row md:items-center gap-6" data-testid="founding-banner">
      <div className="flex-1">
        <div className="label-eyebrow text-[#9E2A2B]">Founding Restaurant Program · {f.spots_left} spots left</div>
        <div className="font-display text-2xl md:text-3xl mt-2">{usd(iv === "year" ? p.year_cents : p.month_cents)}<span className="text-base font-sans text-stone-600">/{iv === "year" ? "yr" : "mo"} per location</span></div>
        <p className="text-sm text-stone-700 mt-2 max-w-2xl">{p.tagline} <b>Founding pricing applies for the first {f.lock_months} months, then the plan renews at the standard public Operations Pro price ($199 a month or $1,990 a year) unless you cancel.</b> Includes a direct product feedback channel and early access to selected improvements.</p>
      </div>
      <button onClick={() => onChoose("founding")} className="h-12 px-6 rounded-full bg-[#9E2A2B] text-white font-semibold hover:bg-[#86231f] shrink-0" data-testid="founding-cta">{cta}</button>
    </div>
  );
}
